import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationResult } from '@azure/msal-browser';
import { UntilDestroy } from '@ngneat/until-destroy';
import { SnackbarService } from '@siat-fe/shared';
import { catchError, EMPTY, switchMap, tap } from 'rxjs';
import { LoginResponse } from '../../models/login.model';
import { User } from '../../models/userinfo.model';
import { AuthenticationService } from '../../services/authentication.service';
import { MenuNavigationService } from '../../services/menu-navigation.service';
import {
  GetIsNotificationRedirect,
  SetIsNotificationRedirect,
} from '../../services/storage-data.utility';
import { UserProvider } from '../../services/user.provider';
import { FirebaseStore } from '../../store/firebase.store';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'lib-entra-authenticator',
  templateUrl: './entra-authenticator.component.html',
})
export class EntraAuthenticatorComponent implements OnInit {
  /**
   * session storage key for the redirect target saved before leaving for entra.
   */
  private readonly ENTRA_REDIRECT_TO_KEY = 'entra_redirect_to';

  /**
   * session storage key for the session-expired flag saved before leaving for entra.
   */
  private readonly ENTRA_SESSION_EXPIRED_KEY = 'entra_session_expired';

  /**
   * session storage key marking that a redirect login was started.
   */
  private readonly ENTRA_START_LOGIN_KEY = 'entra_start_login';

  /**
   * session storage key for the last api response, kept for on-screen debugging.
   */
  private readonly ENTRA_DEBUG_API_RESPONSE_KEY = 'entra_debug_api_response';

  /**
   * current login error message.
   */
  loginError = '';

  /**
   * whether the last failure was a permissions error from the login-entra api.
   */
  permissionsError = false;

  /**
   * whether a redirect/login step is in progress.
   */
  isLoading = true;

  /**
   * last api response, shown for debugging.
   */
  debugApiResponse = '';

  /**
   * injected services.
   */
  route = inject(ActivatedRoute);
  router = inject(Router);
  snackbarService = inject(SnackbarService);
  authService = inject(AuthenticationService);
  menuNavigationService = inject(MenuNavigationService);
  userProvider = inject(UserProvider);
  firebaseStore = inject(FirebaseStore);

  /**
   * @inheritdoc
   */
  ngOnInit(): void {
    this.persistNavigationContext();

    if (this.hasAuthResponseInUrl()) {
      this.handleRedirectResponse();
      return;
    }

    if (this.shouldStartLogin()) {
      this.startRedirectLogin();
      return;
    }

    this.loadDebugState();
  }

  /**
   * saves the redirect target and session-expired flag across the msal redirect round-trip.
   */
  private persistNavigationContext(): void {
    const redirectTo = this.route.snapshot.queryParams['redirectTo'];
    const sessionExpired = this.route.snapshot.queryParams['sessionExpired'];

    if (typeof redirectTo === 'string' && redirectTo.length > 0) {
      sessionStorage.setItem(this.ENTRA_REDIRECT_TO_KEY, redirectTo);
    }

    sessionStorage.setItem(
      this.ENTRA_SESSION_EXPIRED_KEY,
      sessionExpired === 'true' ? 'true' : 'false',
    );
  }

  /**
   * resolves the msal redirect, exchanges it for a siat session, and navigates on success.
   */
  private handleRedirectResponse(): void {
    this.authService
      .handleEntraRedirectResponse()
      .pipe(
        switchMap((authResult: AuthenticationResult | null) => {
          if (!authResult) {
            this.handleEntraLoginError();
            return EMPTY;
          }

          const apiPayload =
            this.authService.buildEntraLoginApiPayload(authResult);
          return this.authService.loginWithEntraApi(apiPayload).pipe(
            switchMap((credential) => {
              const loginResponse: LoginResponse = credential;
              const storedUserCf = this.userProvider.userInfo?.codiceFiscale;
              const isSessionExpired =
                sessionStorage.getItem(this.ENTRA_SESSION_EXPIRED_KEY) ===
                'true';

              this.userProvider.setCredentials(loginResponse);
              return this.authService.getUserInfo(loginResponse.user_id).pipe(
                tap((user: User) => {
                  const currentCf = user?.codiceFiscale || '';
                  const isDifferentUser =
                    !!storedUserCf && storedUserCf !== currentCf;

                  if (isSessionExpired && isDifferentUser) {
                    this.userProvider.clearUserProviderForLogout(true);
                    this.userProvider.setCredentials(loginResponse);
                  }

                  this.userProvider.setCurrentUser(user);
                  this.userProvider.setIsAdmin(user?.administrator || false);
                  this.userProvider.setStruttureList(user.strutture);
                  if (!this.userProvider.isAdminUser) {
                    this.requestPermission();
                  }
                  this.debugApiResponse = JSON.stringify(
                    loginResponse,
                    null,
                    2,
                  );
                  sessionStorage.setItem(
                    this.ENTRA_DEBUG_API_RESPONSE_KEY,
                    this.debugApiResponse,
                  );
                  sessionStorage.removeItem(this.ENTRA_START_LOGIN_KEY);
                  this.handleLoginNavigation(
                    isSessionExpired && isDifferentUser,
                  );
                }),
              );
            }),
            catchError((error) => {
              this.handleLoginEntraApiError(error);
              return EMPTY;
            }),
          );
        }),
        catchError((error) => {
          this.handleEntraLoginError(error);
          return EMPTY;
        }),
      )
      .subscribe();
  }

  /**
   * starts the msal redirect flow, marking it as started so a page reload can resume it.
   */
  private startRedirectLogin(): void {
    sessionStorage.setItem(this.ENTRA_START_LOGIN_KEY, 'true');
    this.authService.startEntraRedirectLogin().catch((error) => {
      this.handleEntraLoginError(error);
    });
  }

  /**
   * checks whether a redirect login should be (re)started.
   * @returns {boolean} true if a login should start.
   */
  private shouldStartLogin(): boolean {
    return (
      this.route.snapshot.queryParams['startLogin'] === 'true' ||
      sessionStorage.getItem(this.ENTRA_START_LOGIN_KEY) === 'true'
    );
  }

  /**
   * checks whether the url hash or query string carries an msal auth response.
   * @returns {boolean} true if the url looks like an msal redirect response.
   */
  private hasAuthResponseInUrl(): boolean {
    const hash = window.location.hash || '';
    const search = window.location.search || '';
    return (
      hash.includes('code=') ||
      hash.includes('error=') ||
      hash.includes('id_token=') ||
      hash.includes('state=') ||
      search.includes('code=') ||
      search.includes('error=') ||
      search.includes('id_token=') ||
      search.includes('state=')
    );
  }

  /**
   * restores the last debug api response from session storage.
   */
  private loadDebugState(): void {
    this.debugApiResponse =
      sessionStorage.getItem(this.ENTRA_DEBUG_API_RESPONSE_KEY) || '';
    this.isLoading = false;
  }

  /**
   * clears the error state and starts a new redirect login attempt.
   */
  retryLogin(): void {
    this.permissionsError = false;
    this.loginError = '';
    this.debugApiResponse = '';
    this.isLoading = false;
    sessionStorage.removeItem(this.ENTRA_DEBUG_API_RESPONSE_KEY);
    this.startRedirectLogin();
  }

  /**
   * clears the entra state and navigates back to the login page.
   */
  goToLogin(): void {
    this.clearEntraState();
    this.router.navigate(['/'], { replaceUrl: true });
  }

  /**
   * sets the generic entra login error state and clears the stale entra state.
   */
  private handleEntraLoginError(error?: unknown): void {
    this.permissionsError = false;
    this.loginError = 'Login Entra fallito';
    this.debugApiResponse = '';
    this.isLoading = false;
    this.clearEntraState();
    if (error) {
      console.error('Errore login Entra', error);
    }
  }

  /**
   * sets the login-entra api permissions error state and clears the stale entra state.
   */
  private handleLoginEntraApiError(error?: unknown): void {
    this.permissionsError = true;
    this.loginError = '';
    this.debugApiResponse = '';
    this.isLoading = false;
    this.clearEntraState();
    this.userProvider.clearUserProviderForLogout(true);
    this.router.navigate(['/'], {
      queryParams: {
        authError: this.getEntraLoginErrorType(error),
      },
      replaceUrl: true,
    });
    if (error) {
      console.error('Errore chiamata login-entra', error);
    }
  }

  /**
   * Maps a login-entra API error to the message shown on the login landing page.
   */
  private getEntraLoginErrorType(error: unknown): 'permissions' | 'generic' {
    return (error as { status?: number })?.status === 401
      ? 'permissions'
      : 'generic';
  }

  /**
   * clears the session storage keys used across the redirect round-trip, including msal's own
   * stale "interaction in progress" flag left behind by an abandoned redirect.
   */
  private clearEntraState(): void {
    sessionStorage.removeItem(this.ENTRA_DEBUG_API_RESPONSE_KEY);
    sessionStorage.removeItem(this.ENTRA_START_LOGIN_KEY);
    sessionStorage.removeItem(this.ENTRA_REDIRECT_TO_KEY);
    sessionStorage.removeItem(this.ENTRA_SESSION_EXPIRED_KEY);
    sessionStorage.removeItem('msal.interaction.status');
  }

  /**
   * navigates to the target page after a successful login, restoring the previous app/struttura context.
   */
  private handleLoginNavigation(isDifferentUserAfterExpiry: boolean): void {
    const redirectTo = sessionStorage.getItem(this.ENTRA_REDIRECT_TO_KEY);
    const isNotificationRoute =
      typeof redirectTo === 'string' &&
      redirectTo.startsWith('/home/notifiche');
    const isNotificationRedirect =
      GetIsNotificationRedirect() || isNotificationRoute;

    if (isDifferentUserAfterExpiry) {
      this.router.navigate(['/apps'], { replaceUrl: true });
      return;
    }

    if (isNotificationRedirect) {
      if (isNotificationRoute) {
        SetIsNotificationRedirect(true);
      }
      this.menuNavigationService.setSelectedApp('home');
      this.router.navigate(['/apps'], { replaceUrl: true });
      return;
    }

    const url = redirectTo ? redirectTo : '/apps';
    if (redirectTo) {
      this.restorePreviousState(redirectTo);
    }

    this.router.navigate([url], { replaceUrl: true });
  }

  /**
   * restores the selected app and struttura context that was active before the redirect.
   */
  private restorePreviousState(redirectTo: string): void {
    const appId = this.getAppId(redirectTo) || '';
    this.menuNavigationService.setSelectedApp(appId);

    const selectedStruttura = this.userProvider.currentStruttura;
    if (selectedStruttura) {
      this.userProvider.setSelectedStruttura(selectedStruttura);
      this.userProvider.setPermissionList(
        selectedStruttura?.permessiVisivi || [],
      );
    }
  }

  /**
   * requests push notification permission and initializes firebase messaging if granted.
   */
  private async requestPermission(): Promise<void> {
    try {
      const permission = await Notification.requestPermission();
      if (permission === 'granted') {
        this.firebaseStore.initFirebase();
      }
    } catch {
      return;
    }
  }

  /**
   * extracts the app id (first path segment) from a redirect url.
   * @returns {string | null} the app id, or null if the path has no segments.
   */
  private getAppId(path: string): string | null {
    const firstSlashIndex = path.indexOf('/');

    if (firstSlashIndex === -1 || firstSlashIndex === path.length - 1) {
      return null;
    }

    const secondSlashIndex = path.indexOf('/', firstSlashIndex + 1);

    if (secondSlashIndex === -1) {
      return path.substring(firstSlashIndex + 1);
    }

    return path.substring(firstSlashIndex + 1, secondSlashIndex);
  }
}
